import React, {useContext} from "react";
import Grid from "@mui/material/Grid2";
import {
    Alert,
    AlertTitle,
    Box,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import {LanguageContext} from "../../components/LanguageContext";

const headerCellSx = {
    fontWeight: 600,
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "text.secondary",
};

const SectionCard = ({icon, title, rows, t}) => (
    <Paper
        sx={(theme) => ({
            p: 0,
            overflow: "hidden",
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
        })}
    >
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={(theme) => ({
                px: 3,
                py: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
            <Box
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: "primary.main",
                })}
            >
                {icon}
            </Box>
            <Typography variant="h6" sx={{fontWeight: 600}}>
                {title}
            </Typography>
        </Stack>

        <Box sx={{overflowX: "auto"}}>
            <Table sx={{minWidth: 720}} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>#</TableCell>
                        <TableCell sx={headerCellSx}>Column</TableCell>
                        <TableCell sx={headerCellSx}>{t.instructionsColType}</TableCell>
                        <TableCell sx={headerCellSx} align="center">{t.instructionsColRequired}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, i) => (
                        <TableRow
                            key={i}
                            sx={(theme) => ({
                                "&:last-child td, &:last-child th": {border: 0},
                                "&:hover": {bgcolor: alpha(theme.palette.primary.main, 0.04)},
                            })}
                        >
                            <TableCell sx={{color: "text.secondary", fontFamily: "monospace", fontSize: "0.85rem"}}>
                                {String.fromCharCode(65 + i)}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    {row.headerName}
                                </Typography>
                                <Typography variant="caption" sx={{color: "text.secondary", fontFamily: "monospace"}}>
                                    {row.translated}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">{row.type}</Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={row.required ? t.instructionsRequiredYes : t.instructionsRequiredNo}
                                    size="small"
                                    color={row.required ? "primary" : "default"}
                                    variant={row.required ? "filled" : "outlined"}
                                    sx={{minWidth: 56}}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    </Paper>
);

const InstructionsPage = () => {
    const {translations: t} = useContext(LanguageContext);

    const monthlyRows = [
        {headerName: "Article code", translated: t.instructionsColArticleCode, type: t.instructionsTypeText, required: true},
        {headerName: "Article name", translated: t.instructionsColArticleName, type: t.instructionsTypeText, required: true},
        {headerName: "Measure Unit", translated: t.instructionsColUnit, type: t.instructionsTypeText, required: true},
        {headerName: "Minimal Supply", translated: t.instructionsColMinSupply, type: t.instructionsTypeNumberPositive, required: false},
        {headerName: "Store code", translated: t.instructionsColStoreCode, type: t.instructionsTypeText, required: false},
        {headerName: "Month", translated: t.instructionsColMonth, type: t.instructionsTypeMonth, required: true},
        {headerName: "Year", translated: t.instructionsColYear, type: t.instructionsTypeYear, required: true},
        {headerName: "Output", translated: t.instructionsColOutput, type: t.instructionsTypeNumberNegativeAllowed, required: true},
    ];

    const weeklyRows = [
        {headerName: "Article code", translated: t.instructionsColArticleCode, type: t.instructionsTypeText, required: true},
        {headerName: "Article name", translated: t.instructionsColArticleName, type: t.instructionsTypeText, required: true},
        {headerName: "Measure Unit", translated: t.instructionsColUnit, type: t.instructionsTypeText, required: true},
        {headerName: "Minimal Supply", translated: t.instructionsColMinSupply, type: t.instructionsTypeNumberPositive, required: false},
        {headerName: "Store code", translated: t.instructionsColStoreCode, type: t.instructionsTypeText, required: false},
        {headerName: "Week", translated: t.instructionsColWeek, type: t.instructionsTypeWeek, required: true},
        {headerName: "Year", translated: t.instructionsColYear, type: t.instructionsTypeYear, required: true},
        {headerName: "Output", translated: t.instructionsColOutput, type: t.instructionsTypeNumberNegativeAllowed, required: true},
    ];

    const fpRows = [
        {headerName: "Article code", translated: t.instructionsColArticleCode, type: t.instructionsTypeText, required: true},
        {headerName: "MAPE", translated: t.instructionsColMap, type: t.instructionsTypeMape, required: true},
        {headerName: "Forecast", translated: t.instructionsColForecast, type: t.instructionsTypeForecastNext, required: true},
    ];

    return (
        <Grid container spacing={3}>
            <Grid size={{xs: 12}}>
                <Paper
                    sx={(theme) => ({
                        p: {xs: 3, md: 4},
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Stack direction={{xs: "column", sm: "row"}} alignItems={{xs: "flex-start", sm: "center"}} spacing={2}>
                        <Box
                            sx={(theme) => ({
                                width: 56,
                                height: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 3,
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                color: "primary.main",
                                flexShrink: 0,
                            })}
                        >
                            <MenuBookOutlinedIcon sx={{fontSize: 32}} />
                        </Box>
                        <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="h4" sx={{fontWeight: 700, letterSpacing: "-0.01em"}}>
                                {t.instructionsTitle}
                            </Typography>
                            <Typography variant="body2" sx={{color: "text.secondary"}}>
                                {t.instructionsSubtitle}
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>
            </Grid>

            <Grid size={{xs: 12}}>
                <Alert
                    severity="info"
                    icon={<LightbulbOutlinedIcon />}
                    sx={{borderRadius: 3, alignItems: "flex-start"}}
                >
                    <AlertTitle sx={{fontWeight: 600}}>{t.instructionsNoteTitle}</AlertTitle>
                    {t.instructionsNoteText}
                </Alert>
            </Grid>

            <Grid size={{xs: 12}}>
                <SectionCard
                    icon={<CalendarMonthOutlinedIcon />}
                    title={t.instructionsMonthlyTemplate}
                    rows={monthlyRows}
                    t={t}
                />
            </Grid>

            <Grid size={{xs: 12}}>
                <SectionCard
                    icon={<DateRangeOutlinedIcon />}
                    title={t.instructionsWeeklyTemplate}
                    rows={weeklyRows}
                    t={t}
                />
            </Grid>

            <Grid size={{xs: 12}}>
                <SectionCard
                    icon={<WorkspacePremiumOutlinedIcon />}
                    title={t.instructionsFpTemplate}
                    rows={fpRows}
                    t={t}
                />
            </Grid>
        </Grid>
    );
};

export default InstructionsPage;
